<script setup>
// Inspector do nó "Enviar mídia": biblioteca de mídias que o Agente pode
// mandar. Você sobe as imagens/arquivos (ou cola uma URL) e dá um nome a cada
// um; o Agente escolhe qual enviar pela descrição. Guarda em data.items =
// [{ id, label, url, type }]. `type` é uma das enums do Attachment
// (image/audio/video/file) e é derivada do MIME do arquivo no upload — quando
// presente, o backend usa direto pra decidir se manda como mídia ou documento
// no WhatsApp; ausente cai no fallback por extensão do URL.
// Sobe vários arquivos de uma vez (em sequência) e mostra as fotos agrupadas
// por modelo (RAPTOR1, RAPTOR2 → RAPTOR): a IA manda o grupo inteiro.
// Quem configura não precisa saber a regra do número: escreve o modelo antes
// de subir (as fotos já entram DOT1, DOT2…), sobe mais fotos direto num grupo,
// renomeia o grupo e move foto de um modelo para outro.
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import mediaAPI from 'dashboard/api/agents/media';
import { assignModel, groupItems, modelNames, nextLabel } from './mediaGroups';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const items = computed(() =>
  Array.isArray(props.data.items) ? props.data.items : []
);
// Enquanto um nome está sendo editado, a arrumação fica congelada (por id):
// senão a mídia trocaria de grupo no meio da digitação e o campo perderia o foco.
const frozenLayout = ref(null);
const sections = computed(() => {
  if (!frozenLayout.value) return groupItems(items.value);
  const byId = new Map(items.value.map(it => [it.id, it]));
  return frozenLayout.value
    .map(section => ({
      name: section.name,
      items: section.ids.map(id => byId.get(id)).filter(Boolean),
    }))
    .filter(section => section.items.length);
});
const freezeLayout = () => {
  frozenLayout.value = groupItems(items.value).map(section => ({
    name: section.name,
    ids: section.items.map(it => it.id),
  }));
};
const releaseLayout = () => {
  frozenLayout.value = null;
};
const uploading = ref(false);
const progress = ref({ done: 0, total: 0 });
const fileInput = ref(null);
const urlDraft = ref('');
// Modelo digitado antes de subir; uploadTarget é o grupo do botão "Mais fotos".
const modelDraft = ref('');
const uploadTarget = ref(null);
const models = computed(() => modelNames(items.value));
const modelPreview = computed(() => {
  const model = modelDraft.value.trim();
  if (!model) return '';
  const first = nextLabel(model, items.value);
  const second = nextLabel(model, [...items.value, { label: first }]);
  return t('AI_AGENTS.FLOW.MEDIA.MODEL_PREVIEW', { first, second });
});

let idSeq = 0;
const genId = () => {
  idSeq += 1;
  return `m_${Date.now().toString(36)}_${idSeq}`;
};
const isImage = it =>
  it?.type === 'image' ||
  (!it?.type && /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(it?.url || ''));

// MIME → enum do Attachment (image/audio/video/file). Alinhado com o
// media_file_type do backend pra que uploads via UI e URLs coladas fiquem
// consistentes.
const typeFromMime = mime => {
  if (!mime) return undefined;
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  return 'file';
};

const commit = next => emit('update', { items: next });

// Segundos entre o 1º balão da resposta e a mídia (Agno::MediaAfterReply, padrão 3).
const DEFAULT_DELAY = 3;
const delaySeconds = computed(() =>
  props.data.delaySeconds === undefined || props.data.delaySeconds === ''
    ? DEFAULT_DELAY
    : props.data.delaySeconds
);
const updateDelay = value => {
  const seconds = Math.min(30, Math.max(0, parseInt(value, 10) || 0));
  emit('update', { delaySeconds: seconds });
};

const updateLabel = (id, label) =>
  commit(items.value.map(it => (it.id === id ? { ...it, label } : it)));

const removeItem = id => commit(items.value.filter(it => it.id !== id));

// Usado direto no @click: lá o argumento é o MouseEvent, não o nome do grupo.
const pickFile = (target = null) => {
  uploadTarget.value = typeof target === 'string' ? target : null;
  fileInput.value?.click();
};

// Renomear o grupo / mover foto: numera de novo depois das que o modelo já tem.
const renamingGroup = ref(null);
const renameDraft = ref('');
const renameInput = ref(null);
const startRename = name => {
  renamingGroup.value = name;
  renameDraft.value = name;
  nextTick(() => renameInput.value?.[0]?.focus());
};
const finishRename = section => {
  const model = renameDraft.value.trim();
  if (renamingGroup.value === null) return;
  renamingGroup.value = null;
  if (!model || model === section.name) return;
  commit(
    assignModel(
      items.value,
      section.items.map(it => it.id),
      model
    )
  );
};

const NEW_MODEL = '__novo';
const newModelFor = ref(null);
const newModelDraft = ref('');
const newModelInput = ref(null);
const onMove = (it, event) => {
  const value = event.target.value;
  event.target.value = '';
  if (value === NEW_MODEL) {
    newModelFor.value = it.id;
    newModelDraft.value = '';
    nextTick(() => newModelInput.value?.[0]?.focus());
  } else if (value) {
    commit(assignModel(items.value, [it.id], value));
  }
};
const confirmNewModel = () => {
  const model = newModelDraft.value.trim();
  if (model) commit(assignModel(items.value, [newModelFor.value], model));
  newModelFor.value = null;
};

// Um por vez: a lista é gravada a cada arquivo, então o que subiu não se
// perde se um falhar no meio.
const onFile = async event => {
  const files = Array.from(event.target.files || []);
  event.target.value = '';
  const model = (uploadTarget.value || modelDraft.value).trim();
  uploadTarget.value = null;
  if (!files.length) return;
  uploading.value = true;
  progress.value = { done: 0, total: files.length };
  let current = [...items.value];
  let failed = 0;
  // eslint-disable-next-line no-restricted-syntax
  for (const file of files) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await mediaAPI.upload(file);
      if (!data.file_url) throw new Error('no url');
      current = [
        ...current,
        {
          id: genId(),
          label: model
            ? nextLabel(model, current)
            : file.name.replace(/\.[^.]+$/, ''),
          url: data.file_url,
          type: typeFromMime(file.type),
        },
      ];
      commit(current);
    } catch {
      failed += 1;
    }
    progress.value = { ...progress.value, done: progress.value.done + 1 };
  }
  uploading.value = false;
  modelDraft.value = '';
  if (failed) {
    useAlert(
      t('AI_AGENTS.FLOW.MEDIA.UPLOAD_FAILED_SOME', {
        failed,
        total: files.length,
      })
    );
  }
};

const addUrl = () => {
  const url = urlDraft.value.trim();
  if (!url) return;
  commit([...items.value, { id: genId(), label: '', url }]);
  urlDraft.value = '';
};
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-11"
        >
          <span class="i-lucide-image text-base" />
        </span>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.MEDIA.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.MEDIA.HINT') }}
    </p>

    <!-- Liga/desliga -->
    <label
      class="mb-3 flex cursor-pointer items-center justify-between rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED') }}
      </span>
      <input
        type="checkbox"
        class="size-4 shrink-0 accent-[#4285f4]"
        :checked="data.enabled !== false"
        @change="emit('update', { enabled: $event.target.checked })"
      />
    </label>

    <!-- Espera entre o 1º balão de texto e a mídia -->
    <label class="mb-3 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.MEDIA.DELAY_LABEL') }}
      <input
        type="number"
        min="0"
        max="30"
        :value="delaySeconds"
        class="mt-1 w-24 rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-1.5 text-sm font-normal text-n-slate-12"
        @change="updateDelay($event.target.value)"
      />
    </label>
    <p class="-mt-2 mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.MEDIA.DELAY_HINT') }}
    </p>

    <!-- Lista de mídias -->
    <div class="space-y-2">
      <div
        v-for="section in sections"
        :key="section.name || '__avulsas'"
        class="space-y-2"
      >
        <div
          v-if="section.name"
          class="flex items-center gap-1.5 pt-1 text-xs font-semibold text-n-slate-11"
        >
          <span class="i-lucide-images shrink-0 text-sm" />
          <input
            v-if="renamingGroup === section.name"
            ref="renameInput"
            v-model="renameDraft"
            class="min-w-0 flex-1 rounded-md border border-n-brand bg-n-solid-2 px-2 py-1 text-xs text-n-slate-12"
            @keydown.enter="$event.target.blur()"
            @keydown.esc="renamingGroup = null"
            @blur="finishRename(section)"
          />
          <template v-else>
            <span class="min-w-0 flex-1 truncate">
              {{
                t('AI_AGENTS.FLOW.MEDIA.GROUP', {
                  name: section.name,
                  count: section.items.length,
                })
              }}
            </span>
            <button
              v-tooltip="t('AI_AGENTS.FLOW.MEDIA.GROUP_RENAME')"
              class="i-lucide-pencil shrink-0 text-n-slate-10 hover:text-n-slate-12"
              @click="startRename(section.name)"
            />
            <button
              class="flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-n-brand hover:bg-n-brand/10 disabled:opacity-60"
              :disabled="uploading"
              @click="pickFile(section.name)"
            >
              <span class="i-lucide-plus text-sm" />
              {{ t('AI_AGENTS.FLOW.MEDIA.GROUP_ADD') }}
            </button>
          </template>
        </div>
        <div
          v-for="it in section.items"
          :key="it.id"
          class="rounded-lg border border-n-weak bg-n-solid-1 p-2"
        >
          <div class="flex items-center gap-2">
            <img
              v-if="isImage(it)"
              :src="it.url"
              class="size-10 shrink-0 rounded-md object-cover"
              alt=""
            />
            <span
              v-else
              class="flex size-10 shrink-0 items-center justify-center rounded-md bg-n-alpha-2 text-n-slate-11"
            >
              <span class="i-lucide-file text-base" />
            </span>
            <div class="min-w-0 flex-1 space-y-1">
              <input
                :value="it.label"
                :placeholder="t('AI_AGENTS.FLOW.MEDIA.LABEL_PH')"
                class="w-full rounded-md border border-n-weak bg-n-solid-2 px-2 py-1.5 text-sm text-n-slate-12"
                @focus="freezeLayout"
                @blur="releaseLayout"
                @input="updateLabel(it.id, $event.target.value)"
              />
              <select
                class="w-full rounded-md border border-n-weak bg-n-solid-2 px-1.5 py-1 text-xs text-n-slate-11"
                @change="onMove(it, $event)"
              >
                <option value="">
                  {{ t('AI_AGENTS.FLOW.MEDIA.MOVE_PH') }}
                </option>
                <option
                  v-for="model in models.filter(m => m !== section.name)"
                  :key="model"
                  :value="model"
                >
                  {{ model }}
                </option>
                <option :value="NEW_MODEL">
                  {{ t('AI_AGENTS.FLOW.MEDIA.MOVE_NEW') }}
                </option>
              </select>
            </div>
            <button
              v-tooltip="t('AI_AGENTS.FLOW.MEDIA.REMOVE')"
              class="i-lucide-trash-2 shrink-0 text-n-ruby-9"
              @click="removeItem(it.id)"
            />
          </div>
          <div
            v-if="newModelFor === it.id"
            class="mt-2 flex items-center gap-2"
          >
            <input
              ref="newModelInput"
              v-model="newModelDraft"
              :placeholder="t('AI_AGENTS.FLOW.MEDIA.NEW_MODEL_PH')"
              class="min-w-0 flex-1 rounded-md border border-n-brand bg-n-solid-2 px-2 py-1.5 text-sm text-n-slate-12"
              @keydown.enter="confirmNewModel"
              @keydown.esc="newModelFor = null"
            />
            <button
              class="shrink-0 rounded-md bg-n-brand px-3 py-1.5 text-xs font-medium text-white"
              @click="confirmNewModel"
            >
              {{ t('AI_AGENTS.FLOW.MEDIA.MOVE_CONFIRM') }}
            </button>
          </div>
        </div>
      </div>

      <p
        v-if="!items.length"
        class="rounded-lg border border-dashed border-n-weak px-3 py-4 text-center text-xs text-n-slate-10"
      >
        {{ t('AI_AGENTS.FLOW.MEDIA.EMPTY') }}
      </p>
    </div>

    <!-- Adicionar -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*,audio/*,video/*,application/pdf"
      multiple
      class="hidden"
      @change="onFile"
    />
    <label class="mt-3 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.MEDIA.MODEL_LABEL') }}
      <input
        v-model="modelDraft"
        :placeholder="t('AI_AGENTS.FLOW.MEDIA.MODEL_PH')"
        class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-1.5 text-sm font-normal text-n-slate-12"
      />
    </label>
    <p class="mt-1 text-xs text-n-slate-10">
      {{ modelPreview || t('AI_AGENTS.FLOW.MEDIA.MODEL_EMPTY') }}
    </p>
    <button
      class="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-n-brand py-2 text-sm font-medium text-n-brand hover:bg-n-brand/10 disabled:opacity-60"
      :disabled="uploading"
      @click="pickFile"
    >
      <span
        :class="
          uploading ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-upload'
        "
      />
      {{
        uploading
          ? t('AI_AGENTS.FLOW.MEDIA.UPLOADING_N', progress)
          : t('AI_AGENTS.FLOW.MEDIA.ADD')
      }}
    </button>

    <div class="mt-2 flex items-center gap-2">
      <input
        v-model="urlDraft"
        :placeholder="t('AI_AGENTS.FLOW.MEDIA.URL_PH')"
        class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-1.5 text-sm"
        @keydown.enter="addUrl"
      />
      <button
        class="shrink-0 rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-slate-11 hover:bg-n-solid-2"
        @click="addUrl"
      >
        {{ t('AI_AGENTS.FLOW.MEDIA.ADD_URL') }}
      </button>
    </div>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
